// The node dashboard — every node as a row, one batch at a time.
//
// Paging is done by the API (`?limit=&offset=`), not by slicing an array here.
// The graph is meant to outgrow this screen, and a table that first downloads
// every row is the part that stops working; asking for a page is the same code
// at 14 nodes and at 14,000.
import React, { useEffect, useState } from "react";
import { formatStamp } from "../utils/datetime";
import graphApi from "../services/graphApi";

const PAGE_SIZES = [15, 25, 50, 100];

const CELL = "px-4 py-2.5 align-middle";
const HEAD =
  "px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400";

/** A header you can sort by. First click sorts DESCENDING.
 *
 * Ascending-first is the usual default, but it is the wrong way round for every
 * column here. For a timestamp, the reason to sort by a date is almost always
 * "what changed lately", and that answer is on the last page under ascending
 * order.
 *
 * There is no Category column: every node is a concept, so a column that
 * reads "concept" on every row says nothing.
 */
function SortHeader({ label, column, sort, order, onSort, className = "" }) {
  const active = sort === column;
  return (
    <th
      scope="col"
      className={`${HEAD} ${className}`}
      aria-sort={active ? (order === "desc" ? "descending" : "ascending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`flex items-center gap-1 uppercase tracking-wider transition hover:text-sky-300 ${
          active ? "text-sky-300" : ""
        }`}
      >
        {label}
        <span aria-hidden="true" className={active ? "" : "opacity-30"}>
          {active ? (order === "desc" ? "\u2193" : "\u2191") : "\u2195"}
        </span>
      </button>
    </th>
  );
}

export default function NodeTable({
  selected = null,
  onSelect = null,
  refreshKey = 0,
  // The subject filter is owned by the page, so one selection governs the
  // drawing, the Next button and this table at once.
  label = null,
}) {
  const [page, setPage] = useState(null);
  const [limit, setLimit] = useState(15);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState("id");
  const [order, setOrder] = useState("asc");
  const [status, setStatus] = useState("loading");

  // A new sort invalidates the page you were on — row 11 under one ordering has
  // nothing to do with row 11 under another — so go back to the first page.
  const sortBy = (column) => {
    setOffset(0);
    if (sort === column) {
      setOrder(order === "desc" ? "asc" : "desc");
    } else {
      setSort(column);
      setOrder("desc"); // newest first on the first click
    }
  };

  useEffect(() => {
    let current = true;
    setStatus("loading");
    graphApi
      .listNodes(limit, offset, sort, order, label)
      .then((data) => {
        if (!current) return;
        setPage(data);
        setStatus("ready");
      })
      .catch(() => current && setStatus("error"));
    return () => {
      current = false;
    };
  }, [limit, offset, sort, order, label, refreshKey]);

  // A new subject invalidates the page you were on — row 20 of one subject has
  // nothing to do with row 20 of another.
  useEffect(() => {
    setOffset(0);
  }, [label]);

  // A shrinking graph can leave the offset past the end — land on the last page
  // that has rows rather than showing an empty table with a live "Prev".
  useEffect(() => {
    if (page && page.total > 0 && offset >= page.total) {
      setOffset(Math.max(0, (Math.ceil(page.total / limit) - 1) * limit));
    }
  }, [page, offset, limit]);

  const total = page?.total ?? 0;
  const items = page?.items ?? [];
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + limit, total);
  const pageNo = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Nodes</h2>
          <p className="mt-1 text-sm text-slate-400">
            Every node in the graph. Click a row to open it above.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          Rows
          <select
            value={limit}
            onChange={(e) => {
              setOffset(0); // a new page size invalidates the old offset
              setLimit(Number(e.target.value));
            }}
            className="rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1 text-slate-200 outline-none transition focus:border-sky-500/40"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {status === "error" && (
        <p className="text-rose-400">Could not load the node list.</p>
      )}

      {/* webapp.md §3: a table is wide content, so it scrolls in its own box and
          never makes the page scroll sideways. */}
      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/30">
        <table className="w-full border-collapse text-sm lg:min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-800">
              <th scope="col" className={`${HEAD} hidden w-16 sm:table-cell`}>
                ID
              </th>
              <th scope="col" className={HEAD}>
                Title
              </th>
              <th scope="col" className={`${HEAD} hidden w-44 lg:table-cell`}>
                Labels
              </th>
              <SortHeader
                label="Created at"
                column="created_at"
                sort={sort}
                order={order}
                onSort={sortBy}
                className="hidden w-48 lg:table-cell"
              />
              <SortHeader
                label="Modified at"
                column="updated_at"
                sort={sort}
                order={order}
                onSort={sortBy}
                className="hidden w-48 md:table-cell"
              />
              <SortHeader
                label="Last opened"
                column="last_opened_at"
                sort={sort}
                order={order}
                onSort={sortBy}
                className="w-48"
              />
            </tr>
          </thead>
          <tbody>
            {status === "loading" && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}

            {status === "ready" && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No nodes yet.
                </td>
              </tr>
            )}

            {items.map((n) => {
              const isSelected = n.slug === selected;
              // A node whose content has changed since it was first synced —
              // the timestamps carry no meaning unless the difference shows.
              const edited = n.updated_at && n.updated_at !== n.created_at;
              return (
                <tr
                  key={n.id}
                  onClick={() => onSelect && onSelect(n.slug)}
                  className={`border-b border-slate-800/60 last:border-0 transition ${
                    onSelect ? "cursor-pointer" : ""
                  } ${
                    isSelected
                      ? "bg-slate-800/60"
                      : "hover:bg-slate-900/80"
                  }`}
                >
                  <td className={`${CELL} hidden tabular-nums text-slate-500 sm:table-cell`}>
                    {n.id}
                  </td>
                  <td className={`${CELL} text-slate-200`}>{n.title}</td>
                  <td className={`${CELL} hidden lg:table-cell`}>
                    <span className="flex flex-wrap gap-1">
                      {(n.labels || []).map((label) => (
                        <span
                          key={label}
                          className="whitespace-nowrap rounded-full border border-slate-700 px-2 py-0.5 text-[11px] text-slate-400"
                        >
                          {label}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td className={`${CELL} hidden whitespace-nowrap text-slate-400 lg:table-cell`}>
                    <time dateTime={n.created_at || undefined}>
                      {formatStamp(n.created_at)}
                    </time>
                  </td>
                  <td
                    className={`${CELL} hidden whitespace-nowrap md:table-cell ${
                      edited ? "text-slate-300" : "text-slate-500"
                    }`}
                  >
                    <time dateTime={n.updated_at || undefined}>
                      {formatStamp(n.updated_at)}
                    </time>
                  </td>
                  {/* Never opened shows an em dash, not a date — a node you
                      have not read yet is the useful thing to spot here. */}
                  <td
                    className={`${CELL} whitespace-nowrap ${
                      n.last_opened_at ? "text-slate-300" : "text-slate-600"
                    }`}
                  >
                    <time dateTime={n.last_opened_at || undefined}>
                      {formatStamp(n.last_opened_at)}
                    </time>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
        <span>
          {total === 0 ? "No nodes" : `${first}–${last} of ${total}`}
          {pageCount > 1 && ` · page ${pageNo} of ${pageCount}`}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1.5 transition enabled:hover:border-sky-500/40 enabled:hover:text-sky-300 disabled:opacity-40"
          >
            ← Prev
          </button>
          <button
            type="button"
            onClick={() => setOffset(offset + limit)}
            disabled={last >= total}
            className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1.5 transition enabled:hover:border-sky-500/40 enabled:hover:text-sky-300 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      </div>
    </section>
  );
}
